import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { assessmentsAPI } from '../../utils/api';
import Button from '../shared/Button';
import './AssessmentDetails.css';
import AdLabPanel from './AdLabPanel';

/**
 * AssessmentDetails Component
 *
 * Displays comprehensive information about a single assessment:
 * - Basic info (name, position, mode, status)
 * - Candidate details
 * - Scheduling and timing
 * - Challenge configuration
 * - Hints settings
 * - Scoring configuration
 * - Status timeline
 * - Sessions (if any)
 * - Actions: Edit, Cancel, Send Invitation, View Results
 */
const AssessmentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadAssessment();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAssessment = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await assessmentsAPI.getById(id);
      setAssessment(response.data.assessment);
    } catch (err) {
      console.error('[AssessmentDetails] Error loading assessment:', err);
      setError(err.response?.data?.error || 'Failed to load assessment details');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/recruiter/assessments');
  };

  const handleEdit = () => {
    navigate(`/recruiter/assessments/${id}/edit`);
  };

  const handleSendInvitation = async () => {
    if (!window.confirm('Send invitation to the candidate?')) {
      return;
    }

    try {
      setActionLoading(true);
      await assessmentsAPI.sendInvitation(id);
      setSuccessMessage('Invitation sent successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      loadAssessment(); // Reload to update status
    } catch (err) {
      console.error('[AssessmentDetails] Error sending invitation:', err);
      setError(err.response?.data?.error || 'Failed to send invitation');
      setTimeout(() => setError(''), 5000);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel this assessment? This action cannot be undone.')) {
      return;
    }

    try {
      setActionLoading(true);
      await assessmentsAPI.cancel(id);
      setSuccessMessage('Assessment cancelled successfully!');
      setTimeout(() => {
        navigate('/recruiter/assessments');
      }, 1500);
    } catch (err) {
      console.error('[AssessmentDetails] Error cancelling assessment:', err);
      setError(err.response?.data?.error || 'Failed to cancel assessment');
      setTimeout(() => setError(''), 5000);
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewResults = () => {
    navigate(`/assessment/${id}/results`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const getStatusBadgeClass = (status) => {
    const baseClass = 'status-badge';
    switch (status) {
      case 'draft': return `${baseClass} status-draft`;
      case 'scheduled': return `${baseClass} status-scheduled`;
      case 'active': return `${baseClass} status-active`;
      case 'in_progress': return `${baseClass} status-in-progress`;
      case 'completed': return `${baseClass} status-completed`;
      case 'cancelled': return `${baseClass} status-cancelled`;
      case 'expired': return `${baseClass} status-expired`;
      default: return baseClass;
    }
  };

  const getStatusText = (status) => {
    return status.replace('_', ' ').toUpperCase();
  };

  const canEdit = (status) => ['draft', 'scheduled'].includes(status);
  const canSendInvitation = (status, invitationSent) => status === 'draft' && !invitationSent;
  const canCancel = (status) => !['completed', 'cancelled'].includes(status);
  const canViewResults = (status) => status === 'completed';

  if (loading) {
    return (
      <div className="assessment-details-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading assessment details...</p>
        </div>
      </div>
    );
  }

  if (error && !assessment) {
    return (
      <div className="assessment-details-container">
        <div className="error-state">
          <h2>Error</h2>
          <p>{error}</p>
          <Button onClick={handleBack} variant="primary">
            Back to Assessments
          </Button>
        </div>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="assessment-details-container">
        <div className="error-state">
          <h2>Assessment Not Found</h2>
          <p>The requested assessment could not be found.</p>
          <Button onClick={handleBack} variant="primary">
            Back to Assessments
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="assessment-details-container">
      {/* Header */}
      <div className="details-header">
        <button className="back-button" onClick={handleBack}>
          &larr; Back to Assessments
        </button>

        <div className="header-content">
          <div className="title-section">
            <div className="title-row">
              <h1>{assessment.name}</h1>
              <span className={getStatusBadgeClass(assessment.status)}>
                {getStatusText(assessment.status)}
              </span>
            </div>
            <p className="position-text">{assessment.position}</p>
          </div>
        </div>

        <div className="header-actions">
          {canEdit(assessment.status) && (
            <Button onClick={handleEdit} variant="outline" disabled={actionLoading}>
              Edit
            </Button>
          )}
          {canSendInvitation(assessment.status, assessment.invitationSent) && (
            <Button onClick={handleSendInvitation} variant="primary" disabled={actionLoading}>
              Send Invitation
            </Button>
          )}
          {canViewResults(assessment.status) && (
            <Button onClick={handleViewResults} variant="success">
              View Results
            </Button>
          )}
          {canCancel(assessment.status) && (
            <Button onClick={handleCancel} variant="danger" disabled={actionLoading}>
              Cancel Assessment
            </Button>
          )}
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="notification success-notification">
          <span className="notification-message">{successMessage}</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="notification error-notification">
          <span className="notification-message">{error}</span>
        </div>
      )}

      {/* Main Content */}
      <div className="details-content">
        {/* Left Column */}
        <div className="left-column">
          {/* Candidate Information */}
          <section className="details-card">
            <h2 className="card-title">Candidate Information</h2>
            <div className="info-grid">
              <div className="info-item">
                <label>Name:</label>
                <span>{assessment.candidateId?.fullName || assessment.candidateId?.email || 'Not specified'}</span>
              </div>
              <div className="info-item">
                <label>Email:</label>
                <span>{assessment.candidateId?.email || 'Not specified'}</span>
              </div>
              <div className="info-item">
                <label>Phone:</label>
                <span>{assessment.candidateId?.phone || 'Not specified'}</span>
              </div>
              <div className="info-item">
                <label>Invitation Sent:</label>
                <span className={assessment.invitationSent ? 'success-text' : 'warning-text'}>
                  {assessment.invitationSent ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
          </section>

          {/* Assessment Configuration */}
          <section className="details-card">
            <h2 className="card-title">Assessment Configuration</h2>
            <div className="info-grid">
              <div className="info-item">
                <label>Mode:</label>
                <span className={`mode-badge mode-${assessment.mode}`}>
                  {assessment.mode.toUpperCase()}
                </span>
              </div>
              <div className="info-item">
                <label>Created:</label>
                <span>{formatDate(assessment.createdAt)}</span>
              </div>
              <div className="info-item">
                <label>Created By:</label>
                <span>{assessment.createdBy?.fullName || assessment.createdBy?.email || 'Unknown'}</span>
              </div>
              {assessment.interviewerId && (
                <div className="info-item">
                  <label>Interviewer:</label>
                  <span>{assessment.interviewerId.fullName || assessment.interviewerId.email || 'Not assigned'}</span>
                </div>
              )}
            </div>
          </section>

          {/* Scheduling */}
          <section className="details-card">
            <h2 className="card-title">Scheduling & Timing</h2>
            <div className="info-grid">
              {assessment.mode === 'live' ? (
                <>
                  <div className="info-item">
                    <label>Scheduled At:</label>
                    <span>{formatDate(assessment.scheduling?.scheduledAt)}</span>
                  </div>
                  <div className="info-item">
                    <label>Duration:</label>
                    <span>{formatDuration(assessment.scheduling?.timeLimit)}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="info-item">
                    <label>Available From:</label>
                    <span>{formatDate(assessment.scheduling?.availableFrom)}</span>
                  </div>
                  <div className="info-item">
                    <label>Available Until:</label>
                    <span>{formatDate(assessment.scheduling?.availableUntil)}</span>
                  </div>
                  <div className="info-item">
                    <label>Time Limit:</label>
                    <span>{formatDuration(assessment.scheduling?.timeLimit)}</span>
                  </div>
                  {assessment.mode === 'async' && (
                    <div className="info-item">
                      <label>Allow Pause:</label>
                      <span>{assessment.scheduling?.allowPause ? 'Yes' : 'No'}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </section>

          {/* Challenges */}
          <section className="details-card">
            <h2 className="card-title">Challenge Configuration</h2>
            <div className="info-grid">
              <div className="info-item">
                <label>Selection Mode:</label>
                <span className="capitalize">{assessment.challenges?.selectionMode || 'Auto'}</span>
              </div>
              {assessment.challenges?.selectionMode === 'auto' && (
                <>
                  <div className="info-item">
                    <label>Difficulty:</label>
                    <span className="capitalize">{assessment.challenges?.difficulty || 'Easy'}</span>
                  </div>
                  <div className="info-item">
                    <label>Count:</label>
                    <span>{assessment.challenges?.count || 3}</span>
                  </div>
                  <div className="info-item">
                    <label>Adaptive:</label>
                    <span>{assessment.challenges?.adaptive ? 'Yes' : 'No'}</span>
                  </div>
                </>
              )}
              {(assessment.populatedChallenges || assessment.challenges?.challengeIds) && (assessment.populatedChallenges?.length > 0 || assessment.challenges?.challengeIds?.length > 0) && (
                <div className="info-item full-width">
                  <label>Selected Challenges:</label>
                  <div className="challenges-list">
                    {(assessment.populatedChallenges || assessment.challenges.challengeIds).map((challenge, index) => (
                      <div key={challenge._id || index} className="challenge-item">
                        {challenge.title || `Challenge ${index + 1}`}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>

        {/* Right Column */}
        <div className="right-column">
          {/* Hints Configuration */}
          <section className="details-card">
            <h2 className="card-title">Hints Configuration</h2>
            <div className="info-grid">
              <div className="info-item">
                <label>Enabled:</label>
                <span className={assessment.hints?.enabled ? 'success-text' : 'muted-text'}>
                  {assessment.hints?.enabled ? 'Yes' : 'No'}
                </span>
              </div>
              {assessment.hints?.enabled && (
                <>
                  <div className="info-item">
                    <label>Max Per Challenge:</label>
                    <span>{assessment.hints.maxPerChallenge || 0}</span>
                  </div>
                  <div className="info-item">
                    <label>Penalty Per Hint:</label>
                    <span>{assessment.hints.penaltyPerHint || 0}%</span>
                  </div>
                  {assessment.mode === 'live' && (
                    <div className="info-item">
                      <label>Require Approval:</label>
                      <span>{assessment.hints.requireApproval ? 'Yes' : 'No'}</span>
                    </div>
                  )}
                  <div className="info-item">
                    <label>Show Penalty Before Reveal:</label>
                    <span>{assessment.hints.showPenaltyBeforeReveal ? 'Yes' : 'No'}</span>
                  </div>
                </>
              )}
            </div>
          </section>

          {/* Scoring Configuration */}
          <section className="details-card">
            <h2 className="card-title">Scoring Configuration</h2>
            <div className="info-grid">
              <div className="info-item">
                <label>Method:</label>
                <span className="capitalize">{assessment.scoring?.method || 'Automated'}</span>
              </div>
              <div className="info-item">
                <label>Pass Threshold:</label>
                <span className="highlight-text">{assessment.scoring?.passThreshold || 70}%</span>
              </div>
            </div>

            <h3 className="subsection-title">Score Components</h3>
            <div className="score-components">
              <div className="component-bar">
                <label>Flags Correct</label>
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${assessment.scoring?.components?.flagsCorrect || 0}%` }}
                  ></div>
                </div>
                <span>{assessment.scoring?.components?.flagsCorrect || 0}%</span>
              </div>
              <div className="component-bar">
                <label>Time Efficiency</label>
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${assessment.scoring?.components?.timeEfficiency || 0}%` }}
                  ></div>
                </div>
                <span>{assessment.scoring?.components?.timeEfficiency || 0}%</span>
              </div>
              <div className="component-bar">
                <label>Methodology</label>
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${assessment.scoring?.components?.methodology || 0}%` }}
                  ></div>
                </div>
                <span>{assessment.scoring?.components?.methodology || 0}%</span>
              </div>
            </div>
          </section>

          {/* Progress & Results (if in progress or completed) */}
          {(assessment.status === 'in_progress' || assessment.status === 'completed') && assessment.results && (
            <section className="details-card">
              <h2 className="card-title">
                {assessment.status === 'completed' ? 'Final Results' : 'Current Progress'}
              </h2>
              <div className="results-summary">
                <div className="result-stat">
                  <label>Overall Score:</label>
                  <span className="score-value">{assessment.results.overallScore || 0}%</span>
                </div>
                <div className="result-stat">
                  <label>Flags Submitted:</label>
                  <span>{assessment.results.flagsSubmitted || 0}</span>
                </div>
                <div className="result-stat">
                  <label>Flags Correct:</label>
                  <span className="success-text">{assessment.results.flagsCorrect || 0}</span>
                </div>
                <div className="result-stat">
                  <label>Hints Used:</label>
                  <span>{assessment.results.hintsUsed || 0}</span>
                </div>
                <div className="result-stat">
                  <label>Time Spent:</label>
                  <span>{formatDuration(assessment.results.timeSpent)}</span>
                </div>
                {assessment.status === 'completed' && (
                  <div className="result-stat">
                    <label>Status:</label>
                    <span className={assessment.results.passed ? 'success-text' : 'danger-text'}>
                      {assessment.results.passed ? 'PASSED' : 'FAILED'}
                    </span>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Timeline */}
          <section className="details-card">
            <h2 className="card-title">Timeline</h2>
            <div className="timeline">
              <div className="timeline-item completed">
                <div className="timeline-marker"></div>
                <div className="timeline-content">
                  <label>Created</label>
                  <span>{formatDate(assessment.createdAt)}</span>
                </div>
              </div>

              {assessment.invitationSent && (
                <div className="timeline-item completed">
                  <div className="timeline-marker"></div>
                  <div className="timeline-content">
                    <label>Invitation Sent</label>
                    <span>{formatDate(assessment.invitationSentAt)}</span>
                  </div>
                </div>
              )}

              {assessment.startedAt && (
                <div className="timeline-item completed">
                  <div className="timeline-marker"></div>
                  <div className="timeline-content">
                    <label>Started</label>
                    <span>{formatDate(assessment.startedAt)}</span>
                  </div>
                </div>
              )}

              {assessment.completedAt && (
                <div className="timeline-item completed">
                  <div className="timeline-marker"></div>
                  <div className="timeline-content">
                    <label>Completed</label>
                    <span>{formatDate(assessment.completedAt)}</span>
                  </div>
                </div>
              )}

              {assessment.status === 'cancelled' && assessment.cancelledAt && (
                <div className="timeline-item cancelled">
                  <div className="timeline-marker"></div>
                  <div className="timeline-content">
                    <label>Cancelled</label>
                    <span>{formatDate(assessment.cancelledAt)}</span>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* One AdLabPanel per scenario for red-team assessments */}
          {['red-team', 'ad-lab'].includes(assessment?.challenges?.selectionMode) && (() => {
            const scenarioKeys = assessment?.challenges?.scenarios?.length
              ? assessment.challenges.scenarios
              : [assessment?.challenges?.scenario || 'ad-lateral-movement'];
            return scenarioKeys.map(key => (
              <AdLabPanel key={key} assessment={assessment} scenarioKey={key} />
            ));
          })()}
        </div>
      </div>
    </div>
  );
};

export default AssessmentDetails;
